interface FetchElevationOptions {
  longitude: number;
  latitude: number;
  signal?: AbortSignal;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

export function parseMapboxElevation(data: unknown): number {
  const features: unknown[] = isRecord(data) && Array.isArray(data.features) ? data.features : [];
  const heights = features.flatMap((feature) => {
    if (!isRecord(feature) || !isRecord(feature.properties)) {
      return [];
    }
    const height = feature.properties.ele;
    return typeof height === 'number' && Number.isFinite(height) ? [height] : [];
  });
  if (!heights.length) {
    throw new Error('No contour elevation is available for this field');
  }
  return Math.max(...heights);
}

export async function fetchMapboxElevation({
  longitude,
  latitude,
  signal,
}: FetchElevationOptions): Promise<number> {
  const token = process.env.REACT_APP_MAPBOX_API_KEY;
  if (!token?.startsWith('pk.')) {
    throw new Error('A browser-public Mapbox token is required for elevation');
  }
  if (
    !Number.isFinite(longitude) ||
    !Number.isFinite(latitude) ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90
  ) {
    throw new Error('Invalid field coordinates for elevation');
  }
  const params = new URLSearchParams({ layers: 'contour', limit: '50', access_token: token });
  const response = await fetch(
    `https://api.mapbox.com/v4/mapbox.mapbox-terrain-v2/tilequery/${longitude},${latitude}.json?${params}`,
    { signal }
  );
  if (!response.ok) {
    throw new Error(`Mapbox elevation request failed (${response.status})`);
  }
  return parseMapboxElevation(await response.json());
}
